import { useCallback, useEffect, useMemo, useRef } from 'react';
import usePredictionStore from '../store/usePredictionStore';
import { fetchPredictableModels, runPrediction } from '../api/predictionApi';
import { getLoggedInOwnerId } from '../../../utils/auth';
import { toQueueItems, nextPending, queueProgress } from '../utils/imageQueue';
import { filterEntries, groupByProject, REQUEST_CONF_FLOOR, visibleDetections } from '../utils/predictionView';

/**
 * Request lifecycle for the Prediction screen.
 *
 * Two things here are load-bearing:
 *
 * - **The run loop reads the queue out of the store on every step, not out of a
 *   captured array.** The user can add, remove or reorder images while a run is
 *   in flight, and a loop iterating a snapshot would keep predicting files that
 *   are no longer on screen and skip ones that are.
 *
 * - **The abort controller is kept in a ref and checked BEFORE each request**,
 *   not only between awaits. The assistant's interview shipped a Stop button
 *   that looked up its controller in a Map by the wrong key and therefore never
 *   aborted anything, for the whole of the longest wait on the card
 *   (2026-09-14). A Stop that does not stop is worse than no Stop.
 */
const usePredictionHook = () => {
  const catalogue = usePredictionStore((s) => s.catalogue);
  const catalogueLoading = usePredictionStore((s) => s.catalogueLoading);
  const catalogueError = usePredictionStore((s) => s.catalogueError);
  const search = usePredictionStore((s) => s.search);
  const selectedVersionId = usePredictionStore((s) => s.selectedVersionId);
  const conf = usePredictionStore((s) => s.conf);
  const images = usePredictionStore((s) => s.images);
  const activeImageId = usePredictionStore((s) => s.activeImageId);
  const rejected = usePredictionStore((s) => s.rejected);
  const running = usePredictionStore((s) => s.running);
  const highlightedDetection = usePredictionStore((s) => s.highlightedDetection);

  const {
    setCatalogue,
    setCatalogueLoading,
    setCatalogueError,
    setSearch,
    setConf,
    setRunning,
    setHighlightedDetection,
    selectVersion,
    addImages,
    setActiveImage,
    patchImage,
    removeImage,
    clearImages,
    resetResults,
  } = usePredictionStore.getState();

  const abortRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // An in-flight request outliving the page would land on a store nobody is
      // rendering, and hold the file it was sent with until it resolved.
      abortRef.current?.abort();
    };
  }, []);

  // ── the catalogue ─────────────────────────────────────────────────────────
  const loadModels = useCallback(async () => {
    const ownerId = getLoggedInOwnerId();
    if (!ownerId) {
      setCatalogueError('noOwner');
      return;
    }
    setCatalogueLoading(true);
    const response = await fetchPredictableModels(ownerId);
    if (!mountedRef.current) return;
    if (response.ok) setCatalogue(response.data);
    else setCatalogueError(response.message);
    setCatalogueLoading(false);
  }, [setCatalogue, setCatalogueError, setCatalogueLoading]);

  useEffect(() => {
    loadModels();
  }, [loadModels]);

  const entries = useMemo(() => catalogue?.models || [], [catalogue]);
  const filtered = useMemo(() => filterEntries(entries, search), [entries, search]);
  const grouped = useMemo(() => groupByProject(filtered), [filtered]);
  const selected = useMemo(
    () => entries.find((entry) => entry.id === selectedVersionId) || null,
    [entries, selectedVersionId],
  );

  // A version that is listed but not runnable must never be run: the request
  // would 404 with a message about the filesystem, which reads as a bug.
  const canRun = Boolean(selected?.runnable) && images.length > 0;

  const activeImage = useMemo(
    () => images.find((item) => item.id === activeImageId) || images[0] || null,
    [images, activeImageId],
  );

  const activeDetections = useMemo(
    () => visibleDetections(activeImage?.result?.detections, conf),
    [activeImage, conf],
  );

  const progress = useMemo(() => queueProgress(images), [images]);

  // ── attaching ─────────────────────────────────────────────────────────────
  const handleAttach = useCallback(
    (input) => {
      const { accepted, rejected: refused } = toQueueItems(input);
      if (!accepted.length && !refused.length) return;
      addImages(accepted, refused);
    },
    [addImages],
  );

  // ── running ───────────────────────────────────────────────────────────────
  const handleRun = useCallback(async () => {
    const state = usePredictionStore.getState();
    const target = state.catalogue?.models?.find((entry) => entry.id === state.selectedVersionId);
    if (!target?.runnable || !state.images.length) return;

    // "Run again" arrives here with every image already `done`, so there is no
    // pending work and the loop below would exit immediately having appeared to
    // do nothing. Reset first — the results are about to be replaced anyway.
    if (!nextPending(state.images)) resetResults();

    const controller = new AbortController();
    abortRef.current = controller;
    setRunning(true);

    try {
      // Re-read the queue every step: see the note at the top of this file.
      for (;;) {
        if (controller.signal.aborted) break;
        const item = nextPending(usePredictionStore.getState().images);
        if (!item) break;

        patchImage(item.id, { status: 'running', error: '' });
        // eslint-disable-next-line no-await-in-loop
        const response = await runPrediction({
          modelType: target.model_type,
          versionId: target.id,
          file: item.file,
          // The FLOOR, not the slider — the slider filters what came back, so
          // dragging it never costs another forward pass.
          conf: REQUEST_CONF_FLOOR,
          signal: controller.signal,
        });

        if (!mountedRef.current) return;
        if (response.canceled || controller.signal.aborted) {
          patchImage(item.id, { status: 'pending' });
          break;
        }
        if (response.ok) patchImage(item.id, { status: 'done', result: response.data, error: '' });
        else patchImage(item.id, { status: 'failed', result: null, error: response.message });
      }
    } finally {
      if (mountedRef.current) setRunning(false);
      abortRef.current = null;
    }
  }, [patchImage, resetResults, setRunning]);

  const handleStop = useCallback(() => {
    abortRef.current?.abort();
    setRunning(false);
  }, [setRunning]);

  const handleSelectVersion = useCallback(
    (entry) => {
      if (!entry) return;
      selectVersion(entry.id);
    },
    [selectVersion],
  );

  return {
    catalogue,
    catalogueLoading,
    catalogueError,
    entries,
    grouped,
    search,
    selected,
    selectedVersionId,
    conf,
    images,
    activeImage,
    activeDetections,
    rejected,
    running,
    canRun,
    progress,
    highlightedDetection,
    handlers: {
      loadModels,
      setSearch,
      setConf,
      setActiveImage,
      setHighlightedDetection,
      handleSelectVersion,
      handleAttach,
      handleRun,
      handleStop,
      removeImage,
      clearImages,
      resetResults,
    },
  };
};

export default usePredictionHook;
